import {
  Box,
  Button,
  Container,
  ContentLayout,
  Header,
  Pagination,
  PropertyFilterProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import {
  defaultVisibleColumns,
  exportSchedulesConfig,
  getFilterSchedulesFilterProperties,
  getItemValue,
  getSchedulesTableDefinition,
  getSearchableFields,
  filterOptions,
  defaultQuery,
} from 'src/pages/schedules/schedulesConfig';
import AppPagination, { AppPaginationRef } from '../AppPagination';
import TablePreferences, { ColumnDefinition } from '../TablePreferences';
import { PaginationConfig } from 'src/contstant/PaginationConfig';
import { ExportButton } from '../ExportButton';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { AuditSchedule } from 'src/types/helios/AuditSchedule';
import routePaths from 'src/contstant/routePaths';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import { useNavigate } from 'react-router-dom';
import FilterModal from '../filter/FilterModel';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import useUserProfile from 'src/hooks/useUserProfile';
import { useUsers } from 'src/store/users/useUsers';
import { useSchedules } from 'src/store/schedules/useSchedules';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { useActivePropertyFilterCount } from 'src/hooks/useActivePropertyFilterCount';
import { getPageFilterKey } from 'src/contstant/storageKeys';
import { usePropertyFilterPersistence } from 'src/hooks/usePropertyFilterPersistence';

interface TableSchedulesProps {
  templateId?: string;
}
const TableSchedules = ({ templateId }: TableSchedulesProps) => {
  const navigate = useNavigate();

  const { getUserProgramId, getUserId, getPersona, getUserBusinessUnitId, getDivisions } =
    useUserProfile();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Stable reference to default filter generator
  const getDefaultFilters = useCallback((): any => {
    return {
      ...defaultQuery, // Use the default query from config (filters to active schedules)
      searchText: '',
    };
  }, []);

  // Use filter persistence hook
  const { filters: queryFilters, setFilters: setQueryFilters } = useFilterPersistence({
    storageKey: getPageFilterKey(businessUnitId, 'schedules'),
    defaultFilters: getDefaultFilters(),
    excludeFields: ['businessUnitId'],
    businessUnitId,
  });

  // Count active filters
  const activeFilterCount = useActivePropertyFilterCount(queryFilters);

  const { schedulesList, fetchSchedulesApiStatus, loadSchedulesList, resetApiStatus } =
    useSchedules();
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Draft query for modal editing (not applied until user clicks Apply)
  const [draftQuery, setDraftQuery] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: 'and',
  });

  const {
    searchText,
    setSearchText: setSearchTextInternal,
    query,
    setQuery,
    filteredItems,
    filterOptions: currentFilterOptions,
    setFilterOptions,
  } = useGenericFilter<AuditSchedule>(
    schedulesList,
    getItemValue,
    getSearchableFields,
    filterOptions,
    undefined, // nestedArrayConfig
    queryFilters.searchText || '', // Use persisted search text from queryFilters
  );

  // Use custom hook for PropertyFilter persistence (search text only, no query tokens for Schedules)
  const setSearchText = usePropertyFilterPersistence(
    setSearchTextInternal,
    setQueryFilters,
    queryFilters,
  );

  const { checkUserPermissions, userPermission } = useUsers();

  const isEditSchedule = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.SCHEDULE.EDIT_SCHEDULE,
      userPermission?.permissions,
    );
  }, [userPermission?.permissions, getPersona]);

  // Sync query with queryFilters (handles both mount and cross-tab sync)
  // Use a ref to track the last applied queryFilters to prevent infinite loops
  const lastAppliedFiltersRef = useRef<string>('');

  useEffect(() => {
    const filtersJson = JSON.stringify(queryFilters);

    // Only update if queryFilters actually changed
    if (filtersJson !== lastAppliedFiltersRef.current) {
      lastAppliedFiltersRef.current = filtersJson;
      setQuery(queryFilters);
      setDraftQuery(queryFilters);
    }
  }, [queryFilters]); // Run when queryFilters changes

  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [permissionAccessConfig.SCHEDULE.EDIT_SCHEDULE],
      divisions: getDivisions(),
    });
    loadData(undefined, undefined);
  }, [getUserBusinessUnitId()]);

  const loadData = (nextToken?: string, maxResults?: number) => {
    loadSchedulesList({
      program: getUserProgramId(),
      maxResults: maxResults,
      nextToken: nextToken,
      businessUnitId: getUserBusinessUnitId(),
      templateId: templateId,
    });
  };

  // Define column preferences
  const tableColumnDefinitions = getSchedulesTableDefinition();

  // Table preferences state
  const [tablePrefs, setTablePrefs] = useState({
    visibleColumns: tableColumnDefinitions.filter((col) =>
      defaultVisibleColumns.includes(col.id),
    ),
    paginatedItems: [],
    currentPage: 1,
    pageSize: PaginationConfig.SCHEDULE_MAX_RESULT,
  });

  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: tablePrefs.pageSize,
    });

  // Handler for table preferences changes
  const handlePreferencesChange = useCallback(
    (data: {
      visibleColumns: any[];
      paginatedItems: any[];
      currentPage: number;
      pageSize: number;
    }) => {
      // Use type assertion to match the expected state type
      setTablePrefs((prev) => ({
        ...prev,
        visibleColumns: data.visibleColumns,
        currentPage: data.currentPage,
        pageSize: data.pageSize as PaginationConfig,
        // Keep the paginatedItems type as is from the previous state
        // but update with new values
        paginatedItems: data.paginatedItems as typeof prev.paginatedItems,
      }));
    },
    [],
  );

  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {/* Refresh data button */}
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => loadData(undefined, undefined)}
          />
          <ExportButton
            data={filteredItems || []}
            config={exportSchedulesConfig}
            onError={(error) => {
              alert(error);
            }}
          />

          {isEditSchedule() && (
            <Button
              variant="normal"
              data-testid="create-schedule-button"
              onClick={() => {
                navigate(routePaths.SCHEDULE_DETAILS.path, {
                  state: { schedule: undefined, templateId: templateId },
                });
              }}
            >
              Create Schedule
            </Button>
          )}
        </SpaceBetween>
      }
    >
      Schedules
    </Header>
  );
  return (
    <ContentLayout>
      <ApiStatusFlashBar
        apiStatus={[fetchSchedulesApiStatus]}
        clearMessages={() => {
          resetApiStatus();
        }}
      />
      <FilterModal
        visible={showFilterModal}
        onDismiss={() => {
          // Reset draft to current query when modal is dismissed without applying
          setDraftQuery(query);
          setShowFilterModal(false);
        }}
        headerText="Filter Schedules"
        query={draftQuery}
        onChange={({ detail }: { detail: PropertyFilterProps.Query }) => {
          // Update draft query only - doesn't affect filtering
          setDraftQuery(detail as any);
        }}
        loading={false}
        filteringProperties={getFilterSchedulesFilterProperties()}
        filteringOptions={currentFilterOptions}
        filteringPlaceholder="Filter Schedules"
        countText={`${filteredItems?.length} matches`}
        onApply={() => {
          // Apply the draft query
          setQuery(draftQuery);
          setQueryFilters({ ...draftQuery, searchText: queryFilters.searchText });
          setShowFilterModal(false);
        }}
      />

      <Table
        data-testid="schedules-table"
        columnDefinitions={tablePrefs.visibleColumns}
        items={paginatedItems || []}
        onRowClick={({ detail }) => {
          navigate(routePaths.SCHEDULE_DETAILS.path, {
            state: { schedule: detail.item },
          });
        }}
        loading={fetchSchedulesApiStatus.isLoading}
        loadingText="Loading Schedules..."
        header={tableHeader}
        filter={
          <SpaceBetween size="m" direction="horizontal">
            <TextFilter
              filteringText={searchText}
              filteringPlaceholder="Search Schedules"
              filteringAriaLabel="Search Schedules"
              onChange={({ detail }) => {
                setSearchText(detail.filteringText);
              }}
            />
            <Button
              data-testid="filter-button"
              iconName="filter"
              onClick={() => {
                // Initialize draft with current query when opening modal
                setDraftQuery(query);
                setShowFilterModal(true);
              }}
            >
              {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
            </Button>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        }
        preferences={
          <TablePreferences
            tableId="schedules-table"
            defaultVisibleColumns={defaultVisibleColumns}
            defaultPageSize={PaginationConfig.SCHEDULE_MAX_RESULT}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            allColumnDefinitions={tableColumnDefinitions}
            allItems={paginatedItems || []}
            onPreferencesChange={handlePreferencesChange}
          />
        }
        stripedRows
        stickyHeader
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <StatusIndicator type="warning">No Schedules.</StatusIndicator>
            </SpaceBetween>
          </Box>
        }
      />
    </ContentLayout>
  );
};

export default TableSchedules;
